import * as React from 'react';
import orderBy from 'lodash/orderBy';
import groupBy from 'lodash/groupBy';

import {ControlBarReact} from 'common/widgets/control-bar';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {Placement} from 'common/components/tooltip/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {defaultOrdering} from 'common/util/function';
import {useTranslation} from 'common/i18n';

import {MetricEntry, QuerySuperEntry} from '../types';
import {ControlBarAction, ControlBarActionItem, ControlBarActionItems, ControlBarActionValue} from '../constants';

/**
 * Groups overflow control items by their `section` property.
 */
function makeOverflowControlSections(items: ControlBarActionItem[]): Array<Section<ControlBarActionValue>> {
  const makeSection = (items?: ControlBarActionItem[]) => {
    if (!items) {
      return null;
    }

    return {
      ...(items[0]?.sectionLabel ? {label: items[0].sectionLabel} : {}),
      items: orderBy(items.filter(Boolean), `order`, `desc`),
    };
  };

  const sectionGroups = groupBy(items, `section`);
  const sectionIds = Object.keys(sectionGroups)
    .map(Number)
    .filter((id) => !Number.isNaN(id))
    .sort(defaultOrdering);
  const lastSectionId = Math.max(...sectionIds);

  const sections = [
    ...sectionIds.map((id) => (id === lastSectionId ? null : makeSection(sectionGroups[id]))),
    // all items without a specified section will display in the middle
    makeSection(sectionGroups[`undefined`]),
    makeSection(sectionGroups[lastSectionId]),
  ].filter(Boolean);

  //Filter out empty sections
  return sections.filter((section) => section.items.length > 0);
}

export function RowActions(props: {
  canDeleteEntry: boolean;
  canAlwaysDeleteEntry: boolean;
  disabled: boolean;
  entry: QuerySuperEntry | MetricEntry;
  hasBehavioralFrequencyFilter: boolean;
  hasNewUI: boolean;
  isEmptyPlaceholder: boolean;
  onControlSelect: (value: ControlBarActionValue) => void;
  onClickDelete: () => void;
  overflowControls: ControlBarActionItem[];
  overflowControlsContextPanelTransform: (item: SectionItem) => ContextPanelData;
  renderActions?: () => React.ReactNode;
}) {
  const {t} = useTranslation();

  const overflowControls = makeOverflowControlSections(props.overflowControls);
  const controls = overflowControls.map((section) => section.items).flat();
  const isDeleteOnly = controls.length === 1 && controls[0].value?.action === ControlBarAction.Delete;
  const showOverflowMenu = controls.length > 0 && !isDeleteOnly;

  const deleteButton = (
    <ButtonReact
      key='entry-delete-action'
      elref='deleteLineButton'
      theme={ButtonTheme.Tertiary}
      icon={props.hasNewUI ? `x` : `trashcan`}
      role='button'
      disabled={props.disabled}
      type={ButtonType.Destructive}
      tooltip={{
        text: t(`query-builder.actions.remove`),
        placement: Placement.Top,
      }}
      onClick={() => props.onClickDelete()}
    />
  );

  return (
    <div className='mp-query-entry-actions'>
      {props.entry.isHidden && (
        <ButtonReact
          key='entry-show-action'
          elref='showEntryButton'
          theme={ButtonTheme.Tertiary}
          icon='eye-hide'
          role='button'
          tooltip={{
            text: t(`query-builder.actions.show-metric`),
            placement: Placement.Top,
          }}
          onClick={() => props.onControlSelect(ControlBarActionItems[ControlBarAction.Show].value)}
        />
      )}

      {props.renderActions && props.renderActions?.()}

      {/** row delete button */}
      {props.canDeleteEntry &&
        !props.hasBehavioralFrequencyFilter &&
        props.hasNewUI &&
        (!props.isEmptyPlaceholder || props.canAlwaysDeleteEntry) &&
        deleteButton}

      {!props.hasBehavioralFrequencyFilter && (
        <div className='mp-query-control-bar-container'>
          {showOverflowMenu && (
            <ControlBarReact
              key='entry-actions'
              elref='lineActions'
              theme={ButtonTheme.Tertiary}
              overflowIcon={props.hasNewUI ? `ellipsis` : `hor-ellipsis`}
              overflowControls={overflowControls}
              overflowMenuWidth={200}
              overflowMenuHeight={0}
              disabled={props.disabled}
              onSelect={(ev: CustomEvent) => props.onControlSelect(ev.detail.value)}
              contextPanelTransform={props.overflowControlsContextPanelTransform}
            />
          )}
        </div>
      )}

      {/** row delete button */}
      {props.canDeleteEntry &&
        !props.hasBehavioralFrequencyFilter &&
        !props.hasNewUI &&
        !props.isEmptyPlaceholder &&
        deleteButton}
    </div>
  );
}
